import React, { useState } from "react";
import {
  Container,
  Navbar,
  Button,
  Offcanvas,
  Nav,
  Tabs,
  Tab,
} from "react-bootstrap";

/**
 * TabsWithLeftOffcanvas_v02
 * - Uses react-bootstrap Tabs/Tab (like your example)
 * - Left Offcanvas serves as the main nav
 * - Top header with hamburger to open the Offcanvas
 * - Simple footer at the bottom
 *
 * Make sure Bootstrap CSS is loaded once in your app entry:
 *   import "bootstrap/dist/css/bootstrap.min.css";
 */

type TabKey = "home" | "profile" | "pricing" | "contact";

interface Props {
  appTitle?: string;
  defaultTab?: TabKey;
}

export const TabsWithLeftOffcanvas_v03: React.FC<Props> = ({
  appTitle = "React Tabs Demo",
  defaultTab = "profile",
}) => {
  const [activeKey, setActiveKey] = useState<TabKey>(defaultTab);
  const [showNav, setShowNav] = useState(false);

  const handleSelect = (k: string | null) => {
    if (!k) return;
    setActiveKey(k as TabKey);
  };

  const handleNavClick = (k: TabKey) => {
    setActiveKey(k);
    setShowNav(false);
  };

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Header */}
      <Navbar bg="light" className="shadow-sm sticky-top">
        <Container fluid className="px-3">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              className="d-inline-flex"
              onClick={() => setShowNav(true)}
              aria-label="Open navigation"
            >
              <span style={{ width: 18, height: 2, background: "currentColor", display: "block", marginBottom: 4 }} />
              <span style={{ width: 18, height: 2, background: "currentColor", display: "block", marginBottom: 4 }} />
              <span style={{ width: 18, height: 2, background: "currentColor", display: "block" }} />
            </Button>
            <Navbar.Brand className="fw-semibold m-0">{appTitle}</Navbar.Brand>
          </div>
        </Container>
      </Navbar>

      {/* Left Offcanvas Nav */}
      <Offcanvas
        show={showNav}
        onHide={() => setShowNav(false)}
        placement="start"
        backdrop
        scroll
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-semibold">Navigation</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav
            activeKey={activeKey}
            className="flex-column"
            onSelect={(k) => handleSelect(k)}
          >
            <Nav.Link
              eventKey="home"
              onClick={() => handleNavClick("home")}
            >
              Home
            </Nav.Link>
            <Nav.Link
              eventKey="profile"
              onClick={() => handleNavClick("profile")}
            >
              Profile
            </Nav.Link>
            <Nav.Link
              eventKey="pricing"
              onClick={() => handleNavClick("pricing")}
            >
              Pricing
            </Nav.Link>
            <Nav.Link
              eventKey="contact"
              onClick={() => handleNavClick("contact")}
              disabled
              aria-disabled
              tabIndex={-1}
            >
              Contact (disabled)
            </Nav.Link>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content */}
      <Container fluid className="flex-grow-1 py-4 px-3 px-md-4">
        {/* Matches the example: react-bootstrap Tabs/Tab with default styling */}
        <Tabs
          id="uncontrolled-tab-example"
          className="mb-3"
          activeKey={activeKey}
          onSelect={handleSelect}
          mountOnEnter
          unmountOnExit
        >
          <Tab eventKey="home" title="Home">
            <div className="p-2">
              Tab content for <strong>Home</strong>.
            </div>
          </Tab>

          <Tab eventKey="profile" title="Profile">
            <div className="p-2">
              Tab content for <strong>Profile</strong>.
            </div>
          </Tab>

          <Tab eventKey="pricing" title="Pricing">
            <div className="p-2">
              <div className="mb-2"><strong>Starter:</strong> $0 — basic features</div>
              <div className="mb-2"><strong>Pro:</strong> $19/mo — advanced tools</div>
              <div className="mb-0"><strong>Enterprise:</strong> Contact sales</div>
            </div>
          </Tab>

          <Tab eventKey="contact" title="Contact" disabled>
            <div className="p-2">
              Tab content for Contact (disabled).
            </div>
          </Tab>
        </Tabs>
      </Container>

      {/* Footer */}
      <footer className="border-top py-3">
        <Container fluid className="px-3">
          <div className="text-center text-secondary small">
            © {new Date().getFullYear()} {appTitle}. All rights reserved.
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default TabsWithLeftOffcanvas_v03;
